<template>
  <transition name="modal-fade">
    <div v-if="isVisible" class="transform-modal-overlay" @click.self="close">
      <div class="transform-modal" @click.stop>
        <!-- Form (header, body, footer) - disparaît en fade au submit -->
        <transition name="form-fade">
          <div v-if="showForm" class="transform-modal-form">
            <div class="transform-modal-header">
              <div class="transform-modal-icon" :class="mode === 'memo' ? 'icon-memo' : 'icon-email'">
                <svg v-if="mode === 'memo'" xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" viewBox="0 0 16 16">
                  <path d="M5 4a.5.5 0 0 0 0 1h6a.5.5 0 0 0 0-1H5zm-.5 2.5A.5.5 0 0 1 5 6h6a.5.5 0 0 1 0 1H5a.5.5 0 0 1-.5-.5zM5 8a.5.5 0 0 0 0 1h6a.5.5 0 0 0 0-1H5zm0 2a.5.5 0 0 0 0 1h3a.5.5 0 0 0 0-1H5z"/>
                  <path d="M2 2a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V2zm10-1H4a1 1 0 0 0-1 1v12a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1V2a1 1 0 0 0-1-1z"/>
                </svg>
                <svg v-else xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" viewBox="0 0 16 16">
                  <path d="M0 4a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2V4Zm2-1a1 1 0 0 0-1 1v.217l7 4.2 7-4.2V4a1 1 0 0 0-1-1H2Zm13 2.383-4.708 2.825L15 11.105V5.383Zm-.034 6.876-5.64-3.471L8 9.583l-1.326-.795-5.64 3.47A1 1 0 0 0 2 13h12a1 1 0 0 0 .966-.741ZM1 11.105l4.708-2.897L1 5.383v5.722Z"/>
                </svg>
              </div>
              <div>
                <h3 class="transform-modal-title">
                  {{ mode === 'memo' ? $t('modals.transform.title.memo') : $t('modals.transform.title.email') }}
                </h3>
                <p class="transform-modal-subtitle">
                  {{ mode === 'memo' ? $t('modals.transform.subtitle.memo') : $t('modals.transform.subtitle.email') }}
                </p>
              </div>
              <button class="transform-modal-close" @click="close">
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="currentColor" viewBox="0 0 16 16">
                  <path d="M2.146 2.854a.5.5 0 1 1 .708-.708L8 7.293l5.146-5.147a.5.5 0 0 1 .708.708L8.707 8l5.147 5.146a.5.5 0 0 1-.708.708L8 8.707l-5.146 5.147a.5.5 0 0 1-.708-.708L7.293 8 2.146 2.854Z"/>
                </svg>
              </button>
            </div>
            <p class="transform-instructions-label" v-html="$t('modals.transform.instructionsLabel')"></p>
            <!-- Search bar : pill (1 ligne) ou stacked (retour à la ligne) -->
            <div class="transform-search-bar" :class="{ 'transform-search-bar-multiline': hasMultipleLines }">
              <textarea
                id="transform-instructions"
                v-model="instructions"
                class="transform-search-input"
                :placeholder="placeholderText"
                rows="1"
                ref="transformSearchBox"
                @input="adjustTransformTextareaHeight"
                @keydown="onInstructionsKeydown"
              ></textarea>
              <button
                type="button"
                class="transform-send-circle"
                :disabled="isLoading"
                @click.stop="submit"
                :title="$t('modals.transform.actions.generate')"
              >
                <svg v-if="isLoading" xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="currentColor" class="spinner-icon" viewBox="0 0 16 16">
                  <path d="M11.534 7h3.932a.25.25 0 0 1 .192.41l-1.966 2.36a.25.25 0 0 1-.384 0l-1.966-2.36a.25.25 0 0 1 .192-.41zm-11 2h3.932a.25.25 0 0 0 .192-.41L2.692 6.23a.25.25 0 0 0-.384 0L.342 8.59A.25.25 0 0 0 .534 9z"/>
                </svg>
                <svg v-else class="arrow-icon" xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M12 19V5M5 12l7-7 7 7" />
                </svg>
              </button>
            </div>
          </div>
        </transition>

        <!-- Result area - visible dès le submit (loading ou résultat) -->
        <transition name="result-fade">
          <div v-if="showResultArea" class="transform-modal-result-wrapper">
            <div class="transform-modal-result" :class="mode === 'email' ? 'transform-modal-result-email' : ''">
              <!-- Header minimal avec close -->
              <div class="result-area-header">
                <button class="transform-modal-close result-close" @click="close">
                  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="currentColor" viewBox="0 0 16 16">
                    <path d="M2.146 2.854a.5.5 0 1 1 .708-.708L8 7.293l5.146-5.147a.5.5 0 0 1 .708.708L8.707 8l5.147 5.146a.5.5 0 0 1-.708.708L8 8.707l-5.146 5.147a.5.5 0 0 1-.708-.708L7.293 8 2.146 2.854Z"/>
                  </svg>
                </button>
              </div>
              <!-- Waiting animation -->
              <div v-if="isLoading" class="result-waiting">
                <div class="waiting-dots">
                  <span></span><span></span><span></span>
                </div>
                <p class="waiting-text">{{ $t('modals.transform.generating') }}</p>
              </div>
              <!-- Result - Mémo -->
              <div v-else-if="currentMemoResult && mode === 'memo'" class="result-content-block">
                <div class="result-header">
                  <h4 class="result-title">{{ $t('modals.transform.result.memoTitle') }}</h4>
                  <div class="version-switcher">
                    <button class="version-arrow" :disabled="currentVersionIndex <= 0" @click="prevVersion" :title="$t('modals.transform.version.previous')">
                      <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="currentColor" viewBox="0 0 16 16"><path d="M11.354 1.646a.5.5 0 0 1 0 .708L5.707 8l5.647 5.646a.5.5 0 0 1-.708.708l-6-6a.5.5 0 0 1 0-.708l6-6a.5.5 0 0 1 .708 0z"/></svg>
                    </button>
                    <span class="version-label">{{ $t('modals.transform.version.label', { current: currentVersionIndex + 1, total: versions.length }) }}</span>
                    <button class="version-arrow" :disabled="currentVersionIndex >= versions.length - 1" @click="nextVersion" :title="$t('modals.transform.version.next')">
                      <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="currentColor" viewBox="0 0 16 16"><path d="M4.646 1.646a.5.5 0 0 1 .708 0l6 6a.5.5 0 0 1 0 .708l-6 6a.5.5 0 0 1-.708-.708L10.293 8 4.646 2.354a.5.5 0 0 1 0-.708z"/></svg>
                    </button>
                  </div>
                  <div class="result-actions">
                <button class="result-action-btn" @click="copyResult" :title="copied ? $t('modals.transform.actions.copied') : $t('common.copy')">
                  <svg v-if="!copied" xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="currentColor" viewBox="0 0 16 16">
                    <path d="M4 1.5H3a2 2 0 0 0-2 2V14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V3.5a2 2 0 0 0-2-2h-1v1h1a1 1 0 0 1 1 1V14a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1V3.5a1 1 0 0 1 1-1h1v-1z"/>
                    <path d="M9.5 1a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-.5.5h-3a.5.5 0 0 1-.5-.5v-1a.5.5 0 0 1 .5-.5h3zm-3-1A1.5 1.5 0 0 0 5 1.5v1A1.5 1.5 0 0 0 6.5 4h3A1.5 1.5 0 0 0 11 2.5v-1A1.5 1.5 0 0 0 9.5 0h-3z"/>
                  </svg>
                  <svg v-else xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="currentColor" viewBox="0 0 16 16">
                    <path d="M12.736 3.97a.733.733 0 0 1 1.047 0c.286.289.29.756.01 1.05L7.88 12.01a.733.733 0 0 1-1.065.02L3.217 8.384a.757.757 0 0 1 0-1.06.733.733 0 0 1 1.047 0l3.052 3.093 5.4-6.425a.247.247 0 0 1 .02-.022Z"/>
                  </svg>
                  {{ copied ? $t('modals.transform.actions.copiedShort') : $t('common.copy') }}
                </button>
                <button class="result-action-btn word-btn" @click="exportToWord" :title="$t('modals.transform.actions.exportWord')">
                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="currentColor" viewBox="0 0 16 16">
                    <path d="M5.485 6.879a.5.5 0 1 0-.97.242l1.5 6a.5.5 0 0 0 .967.01L8 9.402l1.018 3.73a.5.5 0 0 0 .967-.01l1.5-6a.5.5 0 0 0-.97-.242l-1.036 4.144-.997-3.655a.5.5 0 0 0-.964 0l-.997 3.655L5.485 6.88z"/>
                    <path d="M14 14V4.5L9.5 0H4a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2M9.5 3A1.5 1.5 0 0 0 11 4.5h2V14a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1h5.5z"/>
                  </svg>
                  Word
                </button>
              </div>
            </div>
            <div class="result-content legal-text synthesis-content result-content-reveal" v-html="formattedCurrentMemo"></div>
          </div>
          <!-- Result - E-mail (deux boxes Objet + Corps) -->
          <div v-else-if="currentEmailResult && mode === 'email'" class="result-content-block">
            <div class="result-header">
              <h4 class="result-title">{{ $t('modals.transform.result.emailTitle') }}</h4>
              <div class="version-switcher">
                <button class="version-arrow" :disabled="currentVersionIndex <= 0" @click="prevVersion" :title="$t('modals.transform.version.previous')">
                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="currentColor" viewBox="0 0 16 16"><path d="M11.354 1.646a.5.5 0 0 1 0 .708L5.707 8l5.647 5.646a.5.5 0 0 1-.708.708l-6-6a.5.5 0 0 1 0-.708l6-6a.5.5 0 0 1 .708 0z"/></svg>
                </button>
                <span class="version-label">{{ $t('modals.transform.version.label', { current: currentVersionIndex + 1, total: versions.length }) }}</span>
                <button class="version-arrow" :disabled="currentVersionIndex >= versions.length - 1" @click="nextVersion" :title="$t('modals.transform.version.next')">
                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="currentColor" viewBox="0 0 16 16"><path d="M4.646 1.646a.5.5 0 0 1 .708 0l6 6a.5.5 0 0 1 0 .708l-6 6a.5.5 0 0 1-.708-.708L10.293 8 4.646 2.354a.5.5 0 0 1 0-.708z"/></svg>
                </button>
              </div>
            </div>
            <div class="email-boxes">
              <div class="email-box">
                <div class="email-box-header">
                  <span class="email-box-label">{{ $t('modals.transform.email.subject') }}</span>
                  <button class="result-action-btn email-box-btn" @click="copyEmailPart('objet')" :title="copiedObjet ? $t('modals.transform.actions.copied') : $t('common.copy')">
                    <svg v-if="!copiedObjet" xmlns="http://www.w3.org/2000/svg" width="12" height="12" fill="currentColor" viewBox="0 0 16 16">
                      <path d="M4 1.5H3a2 2 0 0 0-2 2V14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V3.5a2 2 0 0 0-2-2h-1v1h1a1 1 0 0 1 1 1V14a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1V3.5a1 1 0 0 1 1-1h1v-1z"/>
                      <path d="M9.5 1a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-.5.5h-3a.5.5 0 0 1-.5-.5v-1a.5.5 0 0 1 .5-.5h3zm-3-1A1.5 1.5 0 0 0 5 1.5v1A1.5 1.5 0 0 0 6.5 4h3A1.5 1.5 0 0 0 11 2.5v-1A1.5 1.5 0 0 0 9.5 0h-3z"/>
                    </svg>
                    <svg v-else xmlns="http://www.w3.org/2000/svg" width="12" height="12" fill="currentColor" viewBox="0 0 16 16">
                      <path d="M12.736 3.97a.733.733 0 0 1 1.047 0c.286.289.29.756.01 1.05L7.88 12.01a.733.733 0 0 1-1.065.02L3.217 8.384a.757.757 0 0 1 0-1.06.733.733 0 0 1 1.047 0l3.052 3.093 5.4-6.425a.247.247 0 0 1 .02-.022Z"/>
                    </svg>
                    {{ copiedObjet ? $t('modals.transform.actions.copiedShort') : $t('common.copy') }}
                  </button>
                </div>
                <div class="email-box-content result-content-reveal">{{ currentEmailResult.objet }}</div>
              </div>
              <div class="email-box">
                <div class="email-box-header">
                  <span class="email-box-label">{{ $t('modals.transform.email.body') }}</span>
                  <button class="result-action-btn email-box-btn" @click="copyEmailPart('contenu')" :title="copiedContenu ? $t('modals.transform.actions.copied') : $t('common.copy')">
                    <svg v-if="!copiedContenu" xmlns="http://www.w3.org/2000/svg" width="12" height="12" fill="currentColor" viewBox="0 0 16 16">
                      <path d="M4 1.5H3a2 2 0 0 0-2 2V14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V3.5a2 2 0 0 0-2-2h-1v1h1a1 1 0 0 1 1 1V14a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1V3.5a1 1 0 0 1 1-1h1v-1z"/>
                      <path d="M9.5 1a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-.5.5h-3a.5.5 0 0 1-.5-.5v-1a.5.5 0 0 1 .5-.5h3zm-3-1A1.5 1.5 0 0 0 5 1.5v1A1.5 1.5 0 0 0 6.5 4h3A1.5 1.5 0 0 0 11 2.5v-1A1.5 1.5 0 0 0 9.5 0h-3z"/>
                    </svg>
                    <svg v-else xmlns="http://www.w3.org/2000/svg" width="12" height="12" fill="currentColor" viewBox="0 0 16 16">
                      <path d="M12.736 3.97a.733.733 0 0 1 1.047 0c.286.289.29.756.01 1.05L7.88 12.01a.733.733 0 0 1-1.065.02L3.217 8.384a.757.757 0 0 1 0-1.06.733.733 0 0 1 1.047 0l3.052 3.093 5.4-6.425a.247.247 0 0 1 .02-.022Z"/>
                    </svg>
                    {{ copiedContenu ? $t('modals.transform.actions.copiedShort') : $t('common.copy') }}
                  </button>
                </div>
                <div class="email-box-content legal-text synthesis-content result-content-reveal" v-html="formattedCurrentEmailContenu"></div>
              </div>
            </div>
          </div>
            <!-- Chatbox pour ajuster (même style que création mémo) -->
            <div v-if="hasResult && !isLoading" class="transform-chatbox">
              <div class="transform-search-bar" :class="{ 'transform-search-bar-multiline': hasAdjustMultipleLines }">
                <textarea
                  v-model="adjustPrompt"
                  class="transform-search-input"
                  :placeholder="$t('modals.transform.adjustPlaceholder')"
                  rows="1"
                  ref="adjustChatboxRef"
                  @input="adjustChatboxTextareaHeight"
                  @keydown="onAdjustKeydown"
                ></textarea>
                <button
                  type="button"
                  class="transform-send-circle"
                  :disabled="isAdjusting || !adjustPrompt.trim()"
                  @click.stop="sendAdjust"
                  :title="$t('modals.transform.actions.adjust')"
                >
                  <svg v-if="isAdjusting" xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="currentColor" class="spinner-icon" viewBox="0 0 16 16">
                    <path d="M11.534 7h3.932a.25.25 0 0 1 .192.41l-1.966 2.36a.25.25 0 0 1-.384 0l-1.966-2.36a.25.25 0 0 1 .192-.41zm-11 2h3.932a.25.25 0 0 0 .192-.41L2.692 6.23a.25.25 0 0 0-.384 0L.342 8.59A.25.25 0 0 0 .534 9z"/>
                  </svg>
                  <svg v-else class="arrow-icon" xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M12 19V5M5 12l7-7 7 7" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>
        </transition>
      </div>
    </div>
  </transition>
</template>

<script>
import { supabase } from '@/supabase/supabaseClient';
import DOMPurify from 'dompurify';
import {
  Document,
  Packer,
  Paragraph,
  TextRun,
  AlignmentType,
  HeadingLevel,
  Table,
  TableRow,
  TableCell,
  WidthType,
  BorderStyle,
} from 'docx';
import { saveAs } from 'file-saver';

export default {
  name: 'TransformModal',

  props: {
    isVisible: {
      type: Boolean,
      default: false,
    },
    mode: {
      type: String, // 'memo' or 'email'
      default: 'memo',
      validator: (v) => ['memo', 'email'].includes(v),
    },
    synthesisText: {
      type: String,
      default: '',
    },
    dossierId: {
      type: String,
      default: '',
    },
  },

  data() {
    return {
      instructions: '',
      isLoading: false,
      resultText: '',
      copied: false,
      error: null,
      emailResult: null,
      copiedObjet: false,
      copiedContenu: false,
      // UX: form vs result area
      showForm: true,
      // Versions (chaque génération ou ajustement = nouvelle version)
      versions: [], // [{ memo: '...' }] ou [{ email: { objet, contenu } }]
      currentVersionIndex: 0,
      // Historique pour l'API d'ajustement
      chatHistory: [], // [{ role, content }]
      adjustPrompt: '',
      isAdjusting: false,
    };
  },

  computed: {
    showResultArea() {
      return this.isLoading || this.hasResult;
    },
    hasResult() {
      return (this.mode === 'memo' && this.resultText) || (this.mode === 'email' && this.emailResult);
    },
    currentMemoResult() {
      if (this.mode !== 'memo' || !this.versions.length) return '';
      const v = this.versions[this.currentVersionIndex];
      return v?.memo ?? '';
    },
    currentEmailResult() {
      if (this.mode !== 'email' || !this.versions.length) return null;
      const v = this.versions[this.currentVersionIndex];
      return v?.email ?? null;
    },
    formattedCurrentMemo() {
      if (!this.currentMemoResult) return '';
      return this.formatSynthesisText(this.currentMemoResult);
    },
    formattedCurrentEmailContenu() {
      if (!this.currentEmailResult || !this.currentEmailResult.contenu) return '';
      return this.formatSynthesisText(this.currentEmailResult.contenu);
    },
    placeholderText() {
      if (this.mode === 'memo') {
        return this.$t('modals.transform.placeholders.memo');
      }
      return this.$t('modals.transform.placeholders.email');
    },
    formattedResult() {
      if (!this.resultText) return '';
      return this.formatSynthesisText(this.resultText);
    },
    formattedEmailContenu() {
      if (!this.emailResult || !this.emailResult.contenu) return '';
      return this.formatSynthesisText(this.emailResult.contenu);
    },
    hasMultipleLines() {
      return this.instructions.includes('\n');
    },
    hasAdjustMultipleLines() {
      return this.adjustPrompt.includes('\n');
    },
  },

  watch: {
    isVisible(val) {
      if (val) {
        document.body.style.overflow = 'hidden';
        this.$nextTick(() => {
          const textarea = this.$el.querySelector('.transform-search-input');
          if (textarea) {
            textarea.focus();
            this.adjustTransformTextareaHeight();
          }
        });
      } else {
        document.body.style.overflow = '';
      }
    },

    mode() {
      this.resetToForm();
    },
  },

  methods: {
    close() {
      this.$emit('close');
      // Ne pas reset : l'état persiste pour permettre de rouvrir et continuer
    },

    onInstructionsKeydown(e) {
      if (e.key === 'Enter') {
        if (e.shiftKey) return; // Shift+Enter = nouvelle ligne
        e.preventDefault();
        this.submit();
      }
    },

    onAdjustKeydown(e) {
      if (e.key === 'Enter') {
        if (e.shiftKey) return; // Shift+Enter = nouvelle ligne
        e.preventDefault();
        this.sendAdjust();
      }
    },

    adjustChatboxTextareaHeight() {
      const el = this.$refs.adjustChatboxRef || this.$el?.querySelector('.transform-chatbox .transform-search-input');
      if (el) {
        el.style.height = 'auto';
        const h = Math.min(el.scrollHeight, 200);
        el.style.height = `${h}px`;
        void el.offsetHeight;
      }
    },

    adjustTransformTextareaHeight() {
      const el = this.$refs.transformSearchBox || this.$el?.querySelector('.transform-search-input');
      if (el) {
        el.style.height = 'auto';
        const h = Math.min(el.scrollHeight, 200);
        el.style.height = `${h}px`;
        void el.offsetHeight; /* force reflow synchrone */
      }
    },

    resetToForm() {
      this.instructions = '';
      this.resultText = '';
      this.emailResult = null;
      this.error = null;
      this.copied = false;
      this.copiedObjet = false;
      this.copiedContenu = false;
      this.showForm = true;
      this.versions = [];
      this.currentVersionIndex = 0;
      this.chatHistory = [];
      this.adjustPrompt = '';
    },

    async submit() {
      if (this.isLoading) return;

      this.isLoading = true;
      this.showForm = false;
      this.error = null;
      this.resultText = '';
      this.emailResult = null;
      this.versions = [];
      this.chatHistory = [];
      this.copied = false;
      this.copiedObjet = false;
      this.copiedContenu = false;

      try {
        const API_URL = process.env.VUE_APP_API_URL || 'https://api.query-juriste.com';
        const endpoint = this.mode === 'memo'
          ? `${API_URL}/copilot/transform-memo`
          : `${API_URL}/copilot/transform-email`;

        const { data: sessionData } = await supabase.auth.getSession();
        const token = sessionData?.session?.access_token;

        const headers = {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` }),
        };

        const response = await fetch(endpoint, {
          method: 'POST',
          headers,
          body: JSON.stringify({
            dossier_id: this.dossierId,
            synthesis: this.synthesisText,
            instructions: this.instructions.trim() || null,
            mode: this.mode,
          }),
        });

        if (!response.ok) {
          throw new Error(this.$t('modals.transform.errors.httpStatus', { status: response.status, statusText: response.statusText }));
        }

        const data = await response.json();
        if (this.mode === 'email') {
          const email = { objet: data.objet || '', contenu: data.contenu || '' };
          if (!email.objet && !email.contenu) throw new Error(this.$t('modals.transform.errors.emptyResponse'));
          this.emailResult = email;
          this.versions = [{ email }];
        } else {
          const memo = data.result || data.text || data.content || '';
          if (!memo) throw new Error(this.$t('modals.transform.errors.emptyResponse'));
          this.resultText = memo;
          this.versions = [{ memo }];
        }
        this.currentVersionIndex = 0;
        this.chatHistory = [
          { role: 'user', content: this.buildInitialPrompt() },
          { role: 'model', content: this.mode === 'memo' ? this.resultText : JSON.stringify(this.emailResult) },
        ];
      } catch (err) {
        console.error(`Error generating ${this.mode}:`, err);
        this.error = err.message || this.$t('errors.unknown');

        const toast = this.$toast || (this.$parent && this.$parent.$toast);
        if (toast) {
          toast.error(this.$t('modals.transform.toasts.generationError', { message: this.error }));
        }
      } finally {
        this.isLoading = false;
      }
    },

    buildInitialPrompt() {
      const instr = this.instructions.trim() ? `\n\nINSTRUCTIONS : ${this.instructions}` : '';
      return `Transforme la synthèse juridique suivante en ${this.mode === 'memo' ? 'mémo professionnel' : 'e-mail professionnel'}.${instr}\n\nSYNTHÈSE :\n${this.synthesisText}`;
    },

    async copyResult() {
      const toCopy = this.formattedCurrentMemo || this.formattedResult;
      if (!toCopy) return;
      try {
        const plainText = this.htmlToPlainText(toCopy);
        const htmlForClipboard = `<!DOCTYPE html><html><head><meta charset="utf-8"></head><body>${toCopy}</body></html>`;
        const clipboardItem = {
          'text/plain': new Blob([plainText], { type: 'text/plain' }),
          'text/html': new Blob([htmlForClipboard], { type: 'text/html' }),
        };
        await navigator.clipboard.write([new ClipboardItem(clipboardItem)]);
        this.copied = true;
        setTimeout(() => { this.copied = false; }, 2000);
      } catch (err) {
        console.error('Copy failed:', err);
        const toast = this.$toast || (this.$parent && this.$parent.$toast);
        if (toast) toast.error(this.$t('modals.transform.toasts.copyError'));
      }
    },

    async copyEmailPart(part) {
      const email = this.currentEmailResult || this.emailResult;
      if (!email) return;
      const text = part === 'objet' ? email.objet : email.contenu;
      if (!text) return;
      try {
        const formatted = part === 'objet' ? null : (this.formattedCurrentEmailContenu || this.formattedEmailContenu);
        const plainText = part === 'objet' ? text : this.htmlToPlainText(formatted);
        const htmlForClipboard = part === 'objet'
          ? `<!DOCTYPE html><html><head><meta charset="utf-8"></head><body><p>${text}</p></body></html>`
          : `<!DOCTYPE html><html><head><meta charset="utf-8"></head><body>${formatted}</body></html>`;
        await navigator.clipboard.write([new ClipboardItem({
          'text/plain': new Blob([plainText], { type: 'text/plain' }),
          'text/html': new Blob([htmlForClipboard], { type: 'text/html' }),
        })]);
        if (part === 'objet') {
          this.copiedObjet = true;
          setTimeout(() => { this.copiedObjet = false; }, 2000);
        } else {
          this.copiedContenu = true;
          setTimeout(() => { this.copiedContenu = false; }, 2000);
        }
      } catch (err) {
        console.error('Copy failed:', err);
        const toast = this.$toast || (this.$parent && this.$parent.$toast);
        if (toast) toast.error(this.$t('modals.transform.toasts.copyError'));
      }
    },

    prevVersion() {
      if (this.currentVersionIndex > 0) this.currentVersionIndex--;
    },
    nextVersion() {
      if (this.currentVersionIndex < this.versions.length - 1) this.currentVersionIndex++;
    },

    async sendAdjust() {
      if (this.isAdjusting || !this.adjustPrompt.trim()) return;
      const prompt = this.adjustPrompt.trim();
      this.adjustPrompt = '';
      this.isAdjusting = true;
      this.chatHistory.push({ role: 'user', content: prompt });

      try {
        const API_URL = process.env.VUE_APP_API_URL || 'https://api.query-juriste.com';
        const endpoint = `${API_URL}/copilot/transform-adjust`;
        const { data: sessionData } = await supabase.auth.getSession();
        const token = sessionData?.session?.access_token;
        const response = await fetch(endpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token && { Authorization: `Bearer ${token}` }),
          },
          body: JSON.stringify({
            dossier_id: this.dossierId,
            synthesis: this.synthesisText,
            mode: this.mode,
            history: this.chatHistory,
          }),
        });
        if (!response.ok) throw new Error(this.$t('modals.transform.errors.httpStatusShort', { status: response.status }));
        const data = await response.json();

        if (this.mode === 'email') {
          const email = { objet: data.objet || '', contenu: data.contenu || '' };
          this.versions.push({ email });
          this.emailResult = email;
        } else {
          const memo = data.result || data.text || data.content || '';
          this.versions.push({ memo });
          this.resultText = memo;
        }
        this.currentVersionIndex = this.versions.length - 1;
        this.chatHistory.push({
          role: 'model',
          content: this.mode === 'memo' ? (this.versions[this.versions.length - 1].memo) : JSON.stringify(this.versions[this.versions.length - 1].email),
        });
      } catch (err) {
        console.error('Adjust error:', err);
        const toast = this.$toast || (this.$parent && this.$parent.$toast);
        if (toast) toast.error(this.$t('modals.transform.toasts.adjustError', { message: err.message }));
      } finally {
        this.isAdjusting = false;
      }
    },

    htmlToPlainText(html) {
      if (!html) return '';
      const div = document.createElement('div');
      div.innerHTML = html;
      let out = '';
      const walk = (node) => {
        for (const c of node.childNodes) {
          if (c.nodeType === 3) {
            out += c.textContent;
          } else if (c.tagName === 'BR') {
            out += '\n';
          } else if (c.tagName === 'LI') {
            out += '\n• ';
            for (const x of c.childNodes) walk(x);
          } else if (c.tagName === 'UL' || c.tagName === 'OL') {
            out += '\n';
            for (const x of c.childNodes) walk(x);
          } else if (c.tagName === 'H3' || c.tagName === 'H2' || c.tagName === 'H1') {
            out += '\n\n';
            for (const x of c.childNodes) walk(x);
            out += '\n\n';
          } else if (c.tagName === 'P') {
            out += '\n';
            for (const x of c.childNodes) walk(x);
          } else {
            for (const x of c.childNodes) walk(x);
          }
        }
      };
      walk(div);
      return out.replace(/\n{3,}/g, '\n\n').trim();
    },

    formatSynthesisText(text) {
      if (!text) return '';
      let formattedText = text;
      formattedText = formattedText.replace(/\n\s*\*\*\*\s*\n/g, '\n\n');
      formattedText = formattedText.replace(/####\s+(.*?)(?:\n|$)/g, '<h3><strong>$1</strong></h3>');
      formattedText = formattedText.replace(/###\s+(.*?)(?:\n|$)/g, '<h3><strong>$1</strong></h3>');
      formattedText = formattedText.replace(/##\s+(.*?)(?:\n|$)/g, '<h3><strong>$1</strong></h3>');
      formattedText = formattedText.replace(/#\s+(.*?)(?:\n|$)/g, '<h3><strong>$1</strong></h3>');
      formattedText = formattedText.replace(
        /(^|\n)( *)\*\s+(.*?)(?=\n|$)/g,
        (match, sep, indent, content) => {
          const level = Math.floor(indent.length / 4) + 1;
          let bullet;
          switch (level) {
            case 1: bullet = '-'; break;
            case 2: bullet = '•'; break;
            case 3: bullet = '◦'; break;
            default: bullet = '▪';
          }
          return `${sep}<li style="margin-left:${level}em; margin-top:1em; margin-bottom:1em;">${bullet} ${content}</li>`;
        }
      );
      formattedText = formattedText.replace(/(<li[\s\S]*?<\/li>)+/g, (m) => `<ul>${m}</ul>`);
      formattedText = formattedText.replace(/<\/ul>\s*<ul>/g, '');
      formattedText = formattedText.replace(/\n/g, '<br>');
      formattedText = formattedText.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
      formattedText = formattedText.replace(/\*([^*]+)\*/g, '<em>$1</em>');
      formattedText = formattedText.replace(/_(.*?)_/g, '<u>$1</u>');
      return DOMPurify.sanitize(formattedText, {
        ALLOWED_TAGS: ['h1', 'h2', 'h3', 'h4', 'strong', 'em', 'u', 'br', 'li', 'ul', 'ol', 'p', 'div', 'span', 'a', 'table', 'thead', 'tbody', 'tr', 'th', 'td'],
        ALLOWED_ATTR: ['style', 'class', 'href', 'target', 'rel'],
      });
    },

    async exportToWord() {
      const text = this.currentMemoResult || this.resultText;
      if (!text) return;
      try {
        const doc = new Document({
          // Sans ce flag, Word étire sur toute la largeur les lignes justifiées
          // terminées par un saut de ligne manuel (<br> -> TextRun break:1)
          compatibility: { doNotExpandShiftReturn: true },
          styles: {
            default: {
              paragraph: {
                run: { size: 22 },
                alignment: AlignmentType.JUSTIFIED,
              },
            },
            paragraphStyles: [{
              id: 'Justified',
              name: 'Justified',
              basedOn: 'Normal',
              next: 'Normal',
              paragraph: {
                alignment: AlignmentType.JUSTIFIED,
                spacing: { before: 0, after: 0, line: 276 },
              },
            }],
          },
          sections: [{
            properties: {},
            children: this.buildDocxFromText(text),
          }],
        });
        const blob = await Packer.toBlob(doc);
        const dateStr = new Date().toISOString().split('T')[0];
        const label = this.mode === 'memo' ? 'Memo' : 'Email';
        saveAs(blob, `${dateStr}_${label}.docx`);
        const toast = this.$toast || (this.$parent && this.$parent.$toast);
        if (toast) toast.success(this.$t('modals.transform.toasts.wordExported'));
      } catch (err) {
        console.error('Word export failed:', err);
        const toast = this.$toast || (this.$parent && this.$parent.$toast);
        if (toast) toast.error(this.$t('modals.transform.toasts.wordExportError'));
      }
    },

    buildDocxFromText(text) {
      if (!text) return [];
      const lines = text.split('\n');
      const elements = [];
      let i = 0;
      while (i < lines.length) {
        const line = lines[i];
        if (line.trim().includes('|') && line.trim().startsWith('|')) {
          const tableResult = this.parseMarkdownTableForDocx(lines, i);
          if (tableResult.table) {
            elements.push(tableResult.table);
            i = tableResult.nextIndex;
            continue;
          }
        }
        if (line.startsWith('# ')) {
          elements.push(new Paragraph({
            heading: HeadingLevel.HEADING_1,
            spacing: { before: 240, after: 120 },
            children: this.parseInlineForDocx(line.substring(2)),
            alignment: AlignmentType.LEFT,
          }));
        } else if (line.startsWith('## ')) {
          elements.push(new Paragraph({
            heading: HeadingLevel.HEADING_2,
            spacing: { before: 200, after: 100 },
            children: this.parseInlineForDocx(line.substring(3)),
            alignment: AlignmentType.LEFT,
          }));
        } else if (line.startsWith('### ')) {
          elements.push(new Paragraph({
            heading: HeadingLevel.HEADING_3,
            spacing: { before: 180, after: 80 },
            children: this.parseInlineForDocx(line.substring(4)),
            alignment: AlignmentType.LEFT,
          }));
        } else if (line.startsWith('#### ')) {
          elements.push(new Paragraph({
            heading: HeadingLevel.HEADING_3,
            spacing: { before: 180, after: 80 },
            children: this.parseInlineForDocx(line.substring(5)),
            alignment: AlignmentType.LEFT,
          }));
        } else {
          const bulletMatch = line.match(/^( *)(?:\*|-)\s(.*)/);
          if (bulletMatch) {
            const indent = bulletMatch[1].length;
            const content = bulletMatch[2];
            const level = Math.floor(indent / 4);
            elements.push(new Paragraph({
              bullet: { level },
              children: this.parseInlineForDocx(content),
              alignment: AlignmentType.JUSTIFIED,
            }));
          } else if (line.trim() === '') {
            elements.push(new Paragraph(''));
          } else {
            elements.push(new Paragraph({
              style: 'Justified',
              children: this.parseInlineForDocx(line),
              alignment: AlignmentType.JUSTIFIED,
            }));
          }
        }
        i++;
      }
      return elements;
    },

    parseMarkdownTableForDocx(lines, startIndex) {
      const tableLines = [];
      let currentIndex = startIndex;
      while (currentIndex < lines.length && lines[currentIndex].trim().includes('|')) {
        tableLines.push(lines[currentIndex]);
        currentIndex++;
      }
      if (tableLines.length < 2) {
        return { table: null, nextIndex: startIndex + 1 };
      }
      const headerLine = tableLines[0];
      const headerCells = headerLine.split('|').map((c) => c.trim()).filter((c) => c !== '');
      const bodyLines = tableLines.slice(2);
      const tableRows = [];
      const headerRow = new TableRow({
        children: headerCells.map((cellContent) =>
          new TableCell({
            children: [new Paragraph({
              children: this.parseInlineForDocx(cellContent),
              alignment: AlignmentType.CENTER,
            })],
            margins: { top: 200, bottom: 200, left: 200, right: 200 },
            shading: { fill: '625CFB', color: 'auto' },
            verticalAlign: 'center',
          })
        ),
        tableHeader: true,
      });
      tableRows.push(headerRow);
      bodyLines.forEach((bodyLine, rowIndex) => {
        const cells = bodyLine.split('|').map((c) => c.trim()).filter((c) => c !== '');
        while (cells.length < headerCells.length) cells.push('');
        const row = new TableRow({
          children: cells.map((cellContent) =>
            new TableCell({
              children: [new Paragraph({
                children: this.parseInlineForDocx(cellContent),
                alignment: AlignmentType.LEFT,
              })],
              margins: { top: 200, bottom: 200, left: 200, right: 200 },
              shading: { fill: rowIndex % 2 === 0 ? 'FFFFFF' : 'f5f5f5' },
              verticalAlign: 'center',
            })
          ),
        });
        tableRows.push(row);
      });
      const table = new Table({
        rows: tableRows,
        width: { size: 9000, type: WidthType.DXA },
        layout: 'autofit',
        borders: {
          top: { style: BorderStyle.SINGLE, size: 4, color: 'bfbfbf' },
          bottom: { style: BorderStyle.SINGLE, size: 4, color: 'bfbfbf' },
          left: { style: BorderStyle.SINGLE, size: 4, color: 'bfbfbf' },
          right: { style: BorderStyle.SINGLE, size: 4, color: 'bfbfbf' },
          insideHorizontal: { style: BorderStyle.SINGLE, size: 4, color: 'bfbfbf' },
          insideVertical: { style: BorderStyle.SINGLE, size: 4, color: 'bfbfbf' },
        },
      });
      return { table, nextIndex: currentIndex };
    },

    parseInlineForDocx(text) {
      if (!text) return [new TextRun({ text: '' })];
      const tokens = text.split(/(\*\*.*?\*\*|_.*?_|\*.*?\*)/g).filter(Boolean);
      return tokens.map((token) => {
        if (/^\*\*(.+)\*\*$/.test(token)) {
          return new TextRun({ text: token.slice(2, -2), bold: true });
        }
        if (/^_(.+)_$/.test(token)) {
          return new TextRun({ text: token.slice(1, -1), underline: { type: 'single' } });
        }
        if (/^\*(.+)\*$/.test(token)) {
          return new TextRun({ text: token.slice(1, -1), italics: true });
        }
        return new TextRun({ text: token });
      });
    },
  },

  beforeDestroy() {
    document.body.style.overflow = '';
  },
};
</script>

<style scoped>
/* ===== OVERLAY ===== */
.transform-modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  padding: 20px;
}

/* ===== MODAL ===== */
.transform-modal {
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.2);
  width: 70%;
  max-height: 85vh;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
}

/* ===== HEADER ===== */
.transform-modal-header {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 24px 24px 16px;
  border-bottom: 1px solid #f0f0f0;
}

.transform-modal-icon {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.icon-memo {
  background: linear-gradient(135deg, #eef2ff, #e0e7ff);
  color: #625CFB;
}

.icon-email {
  background: linear-gradient(135deg, #eef2ff, #e0e7ff);
  color: #625CFB;
}

.transform-modal-title {
  margin: 0;
  font-size: 17px;
  font-weight: 700;
  color: #1a1a2e;
}

.transform-modal-subtitle {
  margin: 2px 0 0;
  font-size: 13px;
  color: #6b7280;
}

.transform-modal-close {
  margin-left: auto;
  background: none;
  border: none;
  padding: 6px;
  border-radius: 8px;
  cursor: pointer;
  color: #9ca3af;
  transition: all 0.15s ease;
  flex-shrink: 0;
}

.transform-modal-close:hover {
  background: #f3f4f6;
  color: #374151;
}

/* ===== SEARCH BAR (pill / capsule) ===== */
.transform-instructions-label {
  margin: 16px 24px 8px;
  font-size: 13px;
  color: #6b7280;
}

.transform-search-bar {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0 12px;
  min-height: 56px;
  background: #f5f5f5;
  border: 1px solid #e0e0e0;
  border-radius: 9999px;
  margin: 0 24px 40px;
  box-sizing: border-box;
}

/* Mode multiligne : textarea au-dessus, bouton en dessous */
.transform-search-bar-multiline {
  flex-direction: column;
  align-items: stretch;
  padding: 12px 12px 12px;
  min-height: auto;
  border-radius: 16px;
  transition: none;
}

.transform-search-bar-multiline .transform-search-input {
  min-height: 120px;
  padding: 12px 0;
  flex: 0 0 auto; /* taille selon le contenu (height inline), pas contraint par flex */
}

.transform-search-bar-multiline .transform-send-circle {
  align-self: flex-end;
}

.transform-search-input {
  flex: 1;
  min-width: 0;
  min-height: 24px;
  max-height: 400px;
  padding: 16px 8px; /* centrage vertical du placeholder (16+24+16=56px) */
  margin: 0;
  border: none;
  outline: none;
  background: transparent;
  font-size: 15px;
  line-height: 24px;
  resize: none;
  overflow-y: auto;
  color: #000;
  transition: none; /* resize instantané, pas d'animation */
}

.transform-search-input:focus {
  outline: none;
  box-shadow: none;
}

.transform-search-input::placeholder {
  color: #9e9e9e;
}

.transform-send-circle {
  width: 40px;
  height: 40px;
  min-width: 40px;
  min-height: 40px;
  padding: 0;
  background: #625CFB;
  color: #fff;
  border: none;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
  flex-shrink: 0;
  box-shadow: 0 2px 8px rgba(98, 92, 251, 0.35);
}

.transform-send-circle:hover:not(:disabled) {
  background: #5147e5;
  box-shadow: 0 4px 12px rgba(98, 92, 251, 0.45);
  transform: translateY(-1px);
}

.transform-send-circle:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.transform-send-circle .arrow-icon {
  stroke-width: 2.5;
  stroke-linecap: round;
}

.spinner-icon {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

/* ===== FORM FADE ===== */
.form-fade-enter-active,
.form-fade-leave-active {
  transition: opacity 0.25s ease;
}
.form-fade-leave-to {
  opacity: 0;
}

/* ===== RESULT AREA ===== */
.transform-modal-result-wrapper {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 200px;
}
.transform-modal-result {
  border-top: 1px solid #f0f0f0;
  padding: 20px 24px 24px;
  flex: 1;
  display: flex;
  flex-direction: column;
}
.result-area-header {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 12px;
}
.result-close {
  margin-left: auto;
}
.result-waiting {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  gap: 16px;
}
.waiting-dots {
  display: flex;
  gap: 8px;
}
.waiting-dots span {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: #625CFB;
  animation: waitingBounce 1.4s ease-in-out infinite both;
}
.waiting-dots span:nth-child(1) { animation-delay: 0s; }
.waiting-dots span:nth-child(2) { animation-delay: 0.2s; }
.waiting-dots span:nth-child(3) { animation-delay: 0.4s; }
@keyframes waitingBounce {
  0%, 80%, 100% { transform: scale(0.6); opacity: 0.5; }
  40% { transform: scale(1); opacity: 1; }
}
.waiting-text {
  margin: 0;
  font-size: 14px;
  color: #6b7280;
}
.result-fade-enter-active {
  transition: opacity 0.3s ease;
}
.result-fade-enter {
  opacity: 0;
}
.version-switcher {
  display: flex;
  align-items: center;
  gap: 8px;
}
.version-arrow {
  background: #f3f4f6;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  padding: 4px 8px;
  cursor: pointer;
  color: #4b5563;
  transition: all 0.15s;
}
.version-arrow:hover:not(:disabled) {
  background: #e5e7eb;
  color: #1f2937;
}
.version-arrow:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}
.version-label {
  font-size: 12px;
  color: #6b7280;
  min-width: 90px;
  text-align: center;
}
/* ===== CHATBOX (même style que création mémo) ===== */
.transform-chatbox {
  margin-top: 20px;
  padding-top: 16px;
  border-top: 1px solid #e5e7eb;
}
.transform-chatbox .transform-search-bar {
  margin: 12px 0 0;
}

.result-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}

.result-title {
  margin: 0;
  font-size: 14px;
  font-weight: 700;
  color: #1a1a2e;
}

.result-actions {
  display: flex;
  gap: 6px;
}

.result-action-btn {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 5px 12px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #fafafa;
  font-size: 12px;
  color: #4b5563;
  cursor: pointer;
  transition: all 0.15s ease;
}

.result-action-btn:hover {
  background: #f3f4f6;
  border-color: #d1d5db;
}

.result-action-btn.word-btn {
  background-color: #2563eb !important;
  border-color: #2563eb;
  color: #fff;
  box-shadow: 0 2px 8px rgba(37, 99, 235, 0.4);
  font-weight: 600;
}

.result-action-btn.word-btn:hover {
  background-color: #1d4ed8 !important;
  border-color: #1d4ed8;
  color: #fff;
  box-shadow: 0 4px 12px rgba(37, 99, 235, 0.5);
}

/* Style identique à la réponse courte (DossierPage) */
.result-content {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  padding: 20px 24px;
  text-align: justify;
  font-size: 16px;
  line-height: 1.5;
  color: #1f2937;
  max-height: 400px;
  overflow-y: auto;
  width: 100%;
}

.result-content :deep(h3) {
  font-size: 17px;
  margin-top: 12px;
  margin-bottom: 6px;
  font-weight: 600;
}

.result-content :deep(ul) {
  margin: 0.5em 0;
  padding-left: 1.5em;
}

.result-content :deep(li) {
  margin: 0.25em 0;
}

.result-content :deep(br) {
  display: block;
  margin-top: 8px;
  content: "";
}

/* ===== EMAIL BOXES ===== */
.transform-modal-result-email .result-title {
  margin-bottom: 16px;
}

.email-boxes {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.email-box {
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  overflow: hidden;
}

.email-box-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 14px;
  background: #f3f4f6;
  border-bottom: 1px solid #e5e7eb;
}

.email-box-label {
  font-size: 12px;
  font-weight: 600;
  color: #4b5563;
}

.email-box-btn {
  padding: 4px 10px;
  font-size: 11px;
}

.email-box-content {
  padding: 14px 18px;
  font-size: 14px;
  line-height: 1.55;
  color: #1f2937;
  max-height: 200px;
  overflow-y: auto;
  white-space: pre-wrap;
}

.email-box-content:not(.legal-text) {
  white-space: pre-wrap;
}

/* Animation progressive : le texte se construit petit à petit */
.result-content-reveal {
  animation: resultReveal 1.2s ease-out forwards;
}

@keyframes resultReveal {
  0% {
    opacity: 0;
    clip-path: inset(0 0 100% 0);
  }
  100% {
    opacity: 1;
    clip-path: inset(0 0 0 0);
  }
}

/* ===== TRANSITIONS ===== */
.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.25s ease;
}
.modal-fade-enter-active .transform-modal,
.modal-fade-leave-active .transform-modal {
  transition: transform 0.25s ease, opacity 0.25s ease;
}

.modal-fade-enter,
.modal-fade-leave-to {
  opacity: 0;
}
.modal-fade-enter .transform-modal,
.modal-fade-leave-to .transform-modal {
  transform: translateY(16px) scale(0.97);
  opacity: 0;
}

.result-slide-enter-active {
  transition: all 0.3s ease;
}
.result-slide-enter {
  opacity: 0;
  transform: translateY(10px);
}

/* ===== RESPONSIVE ===== */
@media (max-width: 640px) {
  .transform-modal-overlay {
    padding: 12px;
    align-items: flex-end;
  }

  .transform-modal {
    max-width: 100%;
    max-height: 90vh;
    border-radius: 16px 16px 0 0;
  }

  .transform-modal-header {
    padding: 20px 18px 14px;
  }

  .transform-search-bar {
    margin: 0 18px 18px;
    padding: 12px 16px;
  }
}
</style>
